import React, { useRef, useSyncExternalStore } from 'react';
import { t } from '@lingui/core/macro';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'src/components/ui';

type PendingConfirmation = { message: string; callback: (ok: boolean) => void };

let pending: PendingConfirmation | null = null;
const listeners = new Set<() => void>();

function setPending(next: PendingConfirmation | null) {
  pending = next;
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The router's `getUserConfirmation`: shows NavigationConfirmModal instead of window.confirm whenever a
 * <Prompt> blocks a navigation. A newer request supersedes an unanswered one, which counts as "stay".
 */
export function getNavigationConfirmation(message: string, callback: (ok: boolean) => void): void {
  pending?.callback(false);
  setPending({ message, callback });
}

function answer(ok: boolean) {
  const current = pending;
  setPending(null);
  current?.callback(ok);
}

/** Mount once inside the router's i18n scope. Closing the modal any other way keeps the user on the page. */
export function NavigationConfirmModal() {
  const current = useSyncExternalStore(subscribe, () => pending);
  // Kept after an answer so the text doesn't blank out while the dialog fades away.
  const lastMessage = useRef('');
  if (current) lastMessage.current = current.message;

  return (
    <Modal isOpen={!!current} toggle={() => answer(false)} size="sm" centered movable={false} resizable={false}>
      <ModalHeader toggle={() => answer(false)}>{t`Unsaved changes`}</ModalHeader>
      <ModalBody>
        <p className="m-0" data-testid="navigation-confirm-message">
          {lastMessage.current}
        </p>
      </ModalBody>
      <ModalFooter className="flex justify-end gap-2">
        <Button color="secondary" outline onClick={() => answer(false)} data-testid="navigation-confirm-stay" autoFocus>
          {t`Stay`}
        </Button>
        <Button color="danger" onClick={() => answer(true)} data-testid="navigation-confirm-leave">
          {t`Leave without saving`}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default NavigationConfirmModal;
